import {
  AbsolutePath,
  AbsolutePathFragment,
  CancellablePromise,
  DisplayStatus,
  FormManager,
  Path,
  Schema,
  SealedFormManagerEvent,
  SealedLocatedValidationIssue,
  SealedValidationIssue,
  SealedValueEvent,
  StateEvent,
  ValidationStatus,
  ValueEvent,
} from "@kform/core";
import * as React from "react";
import { createStore, useStore } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

import { ignorePromiseCancellationException } from "../utils/ignorePromiseCancellationException";
import { arrayFromAsync } from "../utils/iterableUtils";
import { isPromiseLike } from "../utils/promiseUtils";
import { shallow } from "../utils/shallow";
import { useConstant } from "../utils/useConstant";
import { useEqualityFn } from "../utils/useEqualityFn";
import { useLatestValues } from "../utils/useLatestValues";
import { useFormManager } from "./useFormManager";
import { useResolvedPath } from "./useResolvedPath";

/**
 * Options available to the {@link useController} hook.
 */
export interface ControllerOptions<
  T = unknown,
  TState extends ControllerState<T> = ControllerState<T>,
> {
  /**
   * Required if no form context is in scope.
   *
   * If a form context is in scope and this value is also provided, then the
   * provided form manager will be used, in which case the current path of the
   * form context is ignored.
   */
  formManager?: FormManager;
  /**
   * Whether to enable the controller.
   *
   * @default true
   */
  enabled?: boolean;
  /**
   * Default extra state.
   * @internal
   */
  _defaultState?: Partial<TState>;
  /**
   * Function called once the controller has been initialised.
   *
   * @param info Controller's info.
   */
  onInitialized?: (
    state: TState & InitializedControllerState<T>,
  ) => void | PromiseLike<void>;
  /**
   * Function called whenever the controller is uninitialised.
   */
  onUninitialized?: (state: TState & UninitializedControllerState<T>) => void;
  /**
   * Function called whenever an event matching the controller's path is
   * emitted.
   *
   * @param event Form manager event.
   */
  onFormManagerEvent?: (
    event: SealedFormManagerEvent,
    state: TState & InitializedControllerState<T>,
  ) => void | PromiseLike<void>;
  onValueChange?: (
    event: SealedValueEvent,
    state: TState & InitializedControllerState<T>,
  ) => void | PromiseLike<void>;
  onValidationStatusChange?: (
    event: StateEvent.ValidationChange,
    state: TState & InitializedControllerState<T>,
  ) => void | PromiseLike<void>;
  onDisplayStatusChange?: (
    event: StateEvent.DisplayChange,
    state: TState & InitializedControllerState<T>,
  ) => void | PromiseLike<void>;
  onDirtyStatusChange?: (
    event: StateEvent.DirtyChange,
    state: TState & InitializedControllerState<T>,
  ) => void | PromiseLike<void>;
  onTouchedStatusChange?: (
    event: StateEvent.TouchedChange,
    state: TState & InitializedControllerState<T>,
  ) => void | PromiseLike<void>;
}

/**
 * Controller for a value of the form.
 */
export interface Controller<
  T = unknown,
  TState extends ControllerState<T> = ControllerState<T>,
> {
  /**
   * Returns the current state of the controller.
   */
  readonly getState: () => TState;
  /**
   * Sets the (non-private) state of the controller.
   * @param state State to set.
   * @internal
   */
  readonly _setState: (
    state: Partial<TState> | ((state: TState) => Partial<TState>),
  ) => void;
  /**
   * Subscribes to changes in the controller's state.
   *
   * @param selector Selector used to select which part of the controller's
   * state to observe.
   * @param listener Function called whenever the selected state changes.
   * @param options Subscription options.
   * @returns Function which should be called to unsubscribe.
   */
  readonly subscribe: <TSelected = unknown>(
    selector: (state: TState) => TSelected,
    listener: (
      selectedState: TSelected,
      prevSelectedState: TSelected | undefined,
    ) => void,
    options?: ControllerSubscriptionOptions<TSelected>,
  ) => () => void;
  /**
   * Hook used to select part of the controller's state.
   *
   * A component using this hook is re-rendered whenever said part of the state
   * changes.
   * @param selector Selector used to select part of the controller's state.
   */
  readonly useState: (() => TState) &
    (<TResult = unknown>(
      selector: (state: TState) => TResult,
      options?: ControllerUseStateOptions<TResult>,
    ) => TResult);
  /**
   * Hook which returns the form manager being used by the controller.
   */
  readonly useFormManager: () => FormManager;
  /**
   * Hook which returns the schema of the value being controlled.
   */
  readonly useSchema: () => Schema<T>;
  /**
   * Hook which returns the path of the value being controlled by this
   * controller.
   *
   * This path will not contain any provided recursive wildcards.
   */
  readonly usePath: () => AbsolutePath;
  /**
   * Hook which returns the schema path of the value being controlled.
   */
  readonly useSchemaPath: () => AbsolutePath;
  /**
   * Hook which returns whether the controller is currently observing
   * descendants.
   */
  readonly useObservingDescendants: () => boolean;
  /**
   * Hook which returns whether the controller has been initialised.
   */
  readonly useInitialized: () => boolean;
  /**
   * Hook which returns whether a value exists at the path being controlled.
   *
   * Returns `undefined` when the controller is not initialised.
   */
  readonly useExists: () => boolean | undefined;
  /**
   * Hook which returns the form value being controlled.
   *
   * Note that, when observing descendants, this hook will cause a component to
   * rerender when a descendant is changed, even if the identity of the value
   * hasn't changed.
   *
   * Returns `undefined` when the controller is not initialised.
   */
  readonly useValue: () => T | undefined;
  /**
   * Hook which returns whether the value being controlled is dirty.
   *
   * Returns `undefined` when the controller is not initialised.
   */
  readonly useDirty: () => boolean | undefined;
  /**
   * Hook which returns whether the value being controlled is touched.
   *
   * Returns `undefined` when the controller is not initialised.
   */
  readonly useTouched: () => boolean | undefined;
  /**
   * Hook which returns the issues of the value being controlled.
   *
   * Returns `undefined` when the controller is not initialised.
   */
  readonly useIssues: () => SealedValidationIssue[] | undefined;
  /**
   * Hook which returns the validation status of the value being controlled.
   *
   * Returns `undefined` when the controller is not initialised.
   */
  readonly useValidationStatus: () => ValidationStatus | undefined;
  /**
   * Hook which returns the display status of the value being controlled.
   *
   * Returns `undefined` when the controller is not initialised.
   */
  readonly useDisplayStatus: () => DisplayStatus | undefined;

  readonly get: (<TValue = unknown, TResult = unknown>(
    path: Path | string,
    valueHandler: (value: TValue) => TResult | PromiseLike<TResult>,
  ) => CancellablePromise<TResult>) &
    (<TResult = unknown>(
      valueHandler: (value: T) => TResult | PromiseLike<TResult>,
    ) => CancellablePromise<TResult>);
  readonly getClone: (() => CancellablePromise<T>) &
    (<TValue = unknown>(path?: Path | string) => CancellablePromise<TValue>);
  readonly set: ((
    path: Path | string,
    toSet: unknown,
  ) => CancellablePromise<void>) &
    ((toSet: T) => CancellablePromise<void>);
  readonly reset: (path?: Path | string) => CancellablePromise<void>;
  readonly remove: (path?: Path | string) => CancellablePromise<void>;
  readonly validate: (
    path?: Path | string,
  ) => CancellablePromise<SealedLocatedValidationIssue[]>;
  readonly setDirty: (path?: Path | string) => CancellablePromise<void>;
  readonly setPristine: (path?: Path | string) => CancellablePromise<void>;
  readonly setTouched: (path?: Path | string) => CancellablePromise<void>;
  readonly setUntouched: (path?: Path | string) => CancellablePromise<void>;
}

/**
 * Options available to the controller's `useState` hook.
 */
export interface ControllerUseStateOptions<T = unknown> {
  /**
   * Function used to specify when two selections are considered equal to each
   * other.
   * @param v1 First selection.
   * @param v2 Second selection.
   * @returns Whether the two selections are considered equal.
   */
  equalityFn?: (v1: T, v2: T) => boolean;
}

/**
 * Options available when subscribing to the state of the controller.
 */
export interface ControllerSubscriptionOptions<T = unknown> {
  /**
   * Function used to specify when two selections are considered equal to each
   * other.
   * @param v1 First selection.
   * @param v2 Second selection.
   * @returns Whether the two selections are considered equal.
   */
  equalityFn?: (v1: T, v2: T) => boolean;
  /**
   * Whether the subscription's listener should be invoked immediately.
   */
  fireImmediately?: boolean;
}

/**
 * Controller's state.
 */
export type ControllerState<T = unknown> =
  | UninitializedControllerState<T>
  | InitializedControllerState<T>;

/**
 * Base controller's state.
 */
export interface BaseControllerState<T = unknown> {
  /**
   * Form manager being used by the controller.
   */
  readonly formManager: FormManager;
  /**
   * Schema of the form value being controlled.
   */
  readonly schema: Schema<T>;
  /**
   * Path of the form value being controlled.
   */
  readonly path: AbsolutePath;
  /**
   * Schema path of the form value being controller.
   */
  readonly schemaPath: AbsolutePath;
  /**
   * Whether the controller is observing descendants.
   *
   * This will be `true` when the path provided to the controller ends in a
   * recursive wildcard.
   */
  readonly observingDescendants: boolean;
  /**
   * Whether the controller has been initialised.
   */
  readonly initialized: boolean;
  /**
   * Whether the form value being controlled exists.
   */
  readonly exists: boolean | undefined;
  /**
   * Form value.
   */
  readonly value: T | undefined;
  /**
   * Whether the form value being controlled is dirty.
   */
  readonly dirty: boolean | undefined;
  /**
   * Whether the form value being controlled has been touched.
   */
  readonly touched: boolean | undefined;
  /**
   * Validation issues associated with the form value being controlled.
   */
  readonly issues: SealedValidationIssue[] | undefined;
  /**
   * Validation status of the form value being controlled.
   */
  readonly validationStatus: ValidationStatus | undefined;
  /**
   * Display status of the form value being controlled.
   */
  readonly displayStatus: DisplayStatus | undefined;
}

/**
 * Uninitialised controller's state.
 */
export interface UninitializedControllerState<T = unknown>
  extends BaseControllerState<T> {
  readonly initialized: false;
  readonly exists: undefined;
  readonly value: undefined;
  readonly dirty: undefined;
  readonly touched: undefined;
  readonly issues: undefined;
  readonly validationStatus: undefined;
  readonly displayStatus: undefined;
}

/**
 * Initialized controller's state.
 */
export type InitializedControllerState<T = unknown> =
  | NonexistentValueControllerState<T>
  | ExistingValueControllerState<T>;

/**
 * Controller state of a nonexistent form value.
 */
export interface NonexistentValueControllerState<T = unknown>
  extends BaseControllerState<T> {
  readonly initialized: true;
  readonly exists: false;
  readonly value: undefined;
  readonly dirty: undefined;
  readonly touched: undefined;
  readonly issues: undefined;
  readonly validationStatus: undefined;
  readonly displayStatus: undefined;
}

/**
 * Controller state of an existing form value.
 */
export interface ExistingValueControllerState<T = unknown>
  extends BaseControllerState<T> {
  readonly initialized: true;
  readonly exists: true;
  readonly value: T;
  readonly dirty: boolean;
  readonly touched: boolean;
  readonly issues: SealedValidationIssue[];
  readonly validationStatus: ValidationStatus;
  readonly displayStatus: DisplayStatus;
}

/**
 * State internal to the controller's Zustand store.
 *
 * We wrap the stored value within an array so that we can trigger Zustand
 * updates by simply replacing the wrapper, even if the underlying value's
 * identity hasn't changed.
 */
type InternalControllerState<
  T = unknown,
  TState extends ControllerState<T> = ControllerState<T>,
> = Omit<TState, "value"> & {
  value: [T] | undefined;
} & Record<string, unknown>;

/**
 * Value representing an uninitialized controller state.
 */
const UNINITIALIZED_CONTROLLER_STATE = {
  initialized: false,
  exists: undefined,
  value: undefined,
  dirty: undefined,
  touched: undefined,
  issues: undefined,
  validationStatus: undefined,
  displayStatus: undefined,
} as const;

/**
 * Value representing a controller state of a nonexistent form value.
 */
const NONEXISTENT_VALUE_CONTROLLER_STATE = {
  initialized: true,
  exists: false,
  value: undefined,
  dirty: undefined,
  touched: undefined,
  issues: undefined,
  validationStatus: undefined,
  displayStatus: undefined,
} as const;

/**
 * Hook providing access to a controller used to read and control a value of
 * the form.
 * @param path Path of the form value to control, relative to the current path.
 *
 * The path must consist of only identifiers, except for the last fragment,
 * which may be a recursive wildcard to indicate that descendants should also be
 * observed.
 * @param options Available options.
 * @throws {Error} When {@link path} is invalid or contains fragments other than
 * ids.
 * @returns A controller used to read and control the form value.
 */
export function useController<T = unknown>(
  path?: Path | string,
  options?: undefined,
): Controller<T>;
export function useController<
  T = unknown,
  TState extends ControllerState<T> = ControllerState<T>,
>(
  path: Path | string | undefined,
  options: ControllerOptions<T, TState>,
): Controller<T, TState>;
export function useController<
  T = unknown,
  TState extends ControllerState<T> = ControllerState<T>,
>(
  path?: Path | string,
  {
    formManager: formManagerOption,
    enabled = true,
    _defaultState,
    onInitialized,
    onUninitialized,
    onFormManagerEvent,
    onValueChange,
    onValidationStatusChange,
    onDisplayStatusChange,
    onDirtyStatusChange,
    onTouchedStatusChange,
  }: ControllerOptions<T, TState> = {},
): Controller<T, TState> {
  const formManager = useFormManager(formManagerOption);
  const resolvedPath = useResolvedPath(
    path,
    formManagerOption != null ? AbsolutePath.ROOT : undefined,
  );

  // Descendants are being observed when the last fragment of the path is a
  // recursive wildcard
  const observingDescendants =
    resolvedPath.lastFragment === AbsolutePathFragment.RecursiveWildcard;
  // Path of the value being controlled (excluding the possible recursive
  // wildcard)
  const valuePath = React.useMemo(
    () =>
      observingDescendants
        ? new AbsolutePath(resolvedPath.fragments.slice(0, -1))
        : resolvedPath,
    [resolvedPath, observingDescendants],
  );

  // Refuse invalid paths
  if (!formManager.isValidPath(valuePath)) {
    throw new Error(`Invalid path: '${resolvedPath.toString()}'.`);
  }

  // Refuse value paths that don't consist of identifiers only
  if (
    valuePath.fragments.some(
      (frag) => !(frag instanceof AbsolutePathFragment.Id),
    )
  ) {
    throw new Error(
      "Controller path must only contain ids (except for the last fragment, " +
        "which may be a recursive wildcard).",
    );
  }

  // Schema info
  const schemaInfo = React.useMemo(
    () => Array.from(formManager.schemaInfo<T>(valuePath))[0],
    [formManager, valuePath],
  );

  // Controller's Zustand store
  const store = useConstant(() =>
    createStore<InternalControllerState<T, TState>>()(
      subscribeWithSelector(
        () =>
          ({
            formManager,
            schema: schemaInfo.schema,
            path: valuePath,
            schemaPath: schemaInfo.path,
            observingDescendants,
            ...UNINITIALIZED_CONTROLLER_STATE,
            ..._defaultState,
          }) as any,
      ),
    ),
  );

  // Controller functions:
  const controller: Controller<T, TState> = React.useMemo(
    () => ({
      getState: () => unwrapStateValue(store.getState()),
      _setState: (state) => store.setState(state as never),
      subscribe: (selector, listener, options) =>
        store.subscribe(
          (state) => selector(unwrapStateValue(state)),
          listener,
          options,
        ),
      useState: (selector?: any, options?: any) => {
        const hasSelector = typeof selector === "function";
        const result = useStore(
          store,
          useEqualityFn(
            (state) =>
              hasSelector ? selector(unwrapStateValue(state)) : state,
            hasSelector ? options?.equalityFn : shallow,
          ),
        );
        return hasSelector
          ? result
          : unwrapStateValue(result as InternalControllerState);
      },
      useFormManager: () => useStore(store, (state) => state.formManager),
      useSchema: () => useStore(store, (state) => state.schema),
      usePath: () => useStore(store, (state) => state.path),
      useSchemaPath: () => useStore(store, (state) => state.schemaPath),
      useObservingDescendants: () =>
        useStore(store, (state) => state.observingDescendants),
      useInitialized: () => useStore(store, (state) => state.initialized),
      useExists: () => useStore(store, (state) => state.exists),
      useValue: () => useStore(store, (state) => state.value)?.[0],
      useDirty: () => useStore(store, (state) => state.dirty),
      useTouched: () => useStore(store, (state) => state.touched),
      useIssues: () => useStore(store, (state) => state.issues),
      useValidationStatus: () =>
        useStore(store, (state) => state.validationStatus),
      useDisplayStatus: () => useStore(store, (state) => state.displayStatus),
      get: <TValue, TResult>(
        pathOrValueHandler:
          | Path
          | string
          | ((value: T) => TResult | PromiseLike<TResult>),
        valueHandler?: (value: TValue) => TResult | PromiseLike<TResult>,
      ) => {
        const { formManager, path: valuePath } = store.getState();
        return valueHandler !== undefined
          ? formManager.get(
              valuePath.resolve(pathOrValueHandler as Path | string),
              valueHandler,
            )
          : formManager.get(
              valuePath,
              pathOrValueHandler as (
                value: T,
              ) => TResult | PromiseLike<TResult>,
            );
      },
      getClone: <TValue>(path?: Path | string): CancellablePromise<TValue> => {
        const { formManager, path: valuePath } = store.getState();
        return formManager.getClone(
          path != null ? valuePath.resolve(path) : valuePath,
        );
      },
      set: (pathOrToSet: Path | string | T, toSet?: unknown) => {
        const { formManager, path: valuePath } = store.getState();
        return toSet !== undefined
          ? formManager.set(
              valuePath.resolve(pathOrToSet as Path | string),
              toSet,
            )
          : formManager.set(valuePath, pathOrToSet);
      },
      reset: (path?: Path | string) => {
        const { formManager, path: valuePath } = store.getState();
        return formManager.reset(
          path != null ? valuePath.resolve(path) : valuePath,
        );
      },
      remove: (path?: Path | string) => {
        const { formManager, path: valuePath } = store.getState();
        return formManager.remove(
          path != null ? valuePath.resolve(path) : valuePath,
        );
      },
      validate: (path: Path | string = Path.CURRENT_DEEP) => {
        const { formManager, path: valuePath } = store.getState();
        return formManager.validate(
          path != null ? valuePath.resolve(path) : valuePath,
        );
      },
      setDirty: (path?: Path | string) => {
        const { formManager, path: valuePath } = store.getState();
        return formManager.setDirty(
          path != null ? valuePath.resolve(path) : valuePath,
        );
      },
      setPristine: (path?: Path | string) => {
        const { formManager, path: valuePath } = store.getState();
        return formManager.setPristine(
          path != null ? valuePath.resolve(path) : valuePath,
        );
      },
      setTouched: (path?: Path | string) => {
        const { formManager, path: valuePath } = store.getState();
        return formManager.setTouched(
          path != null ? valuePath.resolve(path) : valuePath,
        );
      },
      setUntouched: (path?: Path | string) => {
        const { formManager, path: valuePath } = store.getState();
        return formManager.setUntouched(
          path != null ? valuePath.resolve(path) : valuePath,
        );
      },
    }),
    [store],
  );

  // Store latest values and listeners used during API calls or event handling
  const latestValues = useLatestValues({
    onInitialized,
    onUninitialized,
    onFormManagerEvent,
    onValueChange,
    onValidationStatusChange,
    onDisplayStatusChange,
    onDirtyStatusChange,
    onTouchedStatusChange,
  });

  // Initialise controller
  React.useEffect(() => {
    store.setState({
      formManager,
      schema: schemaInfo.schema,
      path: valuePath,
      schemaPath: schemaInfo.path,
    } as never);

    if (!enabled) {
      return;
    }

    let cleanedUp = false;
    let unsubscribe: (() => CancellablePromise<void>) | undefined = undefined;
    const initPromise = formManager
      .info<T, void>(valuePath, async (infoIterable) => {
        if (cleanedUp) {
          return;
        }

        unsubscribe = await formManager.subscribe(resolvedPath, (event) => {
          // Unsubscribing is asynchronous, so it's possible that an event is
          // emitted after cleaning up, but before unsubscribing, which we
          // should ignore
          if (cleanedUp) {
            return;
          }

          let specificEventHandlerResult: void | PromiseLike<void> = undefined;
          if (event instanceof ValueEvent) {
            if (!observingDescendants || event.path.equals(valuePath)) {
              if (event instanceof ValueEvent.Init) {
                store.setState({
                  initialized: true,
                  exists: true,
                  value: [event.value as T],
                  dirty: false,
                  touched: false,
                  issues: [],
                  validationStatus: "unvalidated",
                  displayStatus: "valid",
                } as never);
              } else if (event instanceof ValueEvent.Destroy) {
                store.setState(NONEXISTENT_VALUE_CONTROLLER_STATE as never);
              } else {
                // Force Zustand update even if value's identity hasn't changed
                store.setState({ value: [event.value as T] } as never);
              }
            } else {
              // Force Zustand update even if value's identity hasn't changed
              store.setState(
                (state) =>
                  ({
                    value: state.value ? [state.value[0]] : undefined,
                  }) as never,
              );
            }
            specificEventHandlerResult = latestValues.onValueChange?.(
              event,
              controller.getState() as never,
            );
          } else if (event instanceof StateEvent.ValidationChange) {
            if (!observingDescendants || event.path.equals(valuePath)) {
              store.setState({
                issues: event.issues,
                validationStatus: event.status,
              } as never);
            }
            specificEventHandlerResult =
              latestValues.onValidationStatusChange?.(
                event,
                controller.getState() as never,
              );
          } else if (event instanceof StateEvent.DisplayChange) {
            if (!observingDescendants || event.path.equals(valuePath)) {
              store.setState({ displayStatus: event.status } as never);
            }
            specificEventHandlerResult = latestValues.onDisplayStatusChange?.(
              event,
              controller.getState() as never,
            );
          } else if (event instanceof StateEvent.DirtyChange) {
            if (!observingDescendants || event.path.equals(valuePath)) {
              store.setState({ dirty: event.status } as never);
            }
            specificEventHandlerResult = latestValues.onDirtyStatusChange?.(
              event,
              controller.getState() as never,
            );
          } else if (event instanceof StateEvent.TouchedChange) {
            if (!observingDescendants || event.path.equals(valuePath)) {
              store.setState({ touched: event.status } as never);
            }
            specificEventHandlerResult = latestValues.onTouchedStatusChange?.(
              event,
              controller.getState() as never,
            );
          }

          // Results of handling the event
          const formManagerEventHandlerResult =
            latestValues.onFormManagerEvent?.(
              event,
              controller.getState() as never,
            );

          // Return a promise if either of the handler results is a promise
          if (
            isPromiseLike(formManagerEventHandlerResult) ||
            isPromiseLike(specificEventHandlerResult)
          ) {
            return Promise.all([
              formManagerEventHandlerResult,
              specificEventHandlerResult,
            ]).then(() => {});
          }
        });

        const info = (await arrayFromAsync(infoIterable))[0];
        store.setState(
          (info
            ? {
                initialized: true,
                exists: true,
                value: [info.value],
                dirty: info.dirty,
                touched: info.touched,
                issues: info.issues,
                validationStatus: info.validationStatus,
                displayStatus: info.displayStatus,
              }
            : NONEXISTENT_VALUE_CONTROLLER_STATE) as never,
        );
        return latestValues.onInitialized?.(controller.getState() as never);
      })
      .catch(ignorePromiseCancellationException);

    return () => {
      cleanedUp = true;
      initPromise?.cancel(
        `Clean up 'useEffect' access to info of '${valuePath.toString()}'.`,
      );
      void unsubscribe?.();
      store.setState(UNINITIALIZED_CONTROLLER_STATE as never);
      latestValues.onUninitialized?.(controller.getState() as never);
    };
  }, [
    controller,
    enabled,
    formManager,
    latestValues,
    observingDescendants,
    resolvedPath,
    schemaInfo.path,
    schemaInfo.schema,
    store,
    valuePath,
  ]);

  return controller;
}

/**
 * Unwraps the value within the controller's state.
 */
function unwrapStateValue<
  T = unknown,
  TState extends ControllerState<T> = ControllerState<T>,
>(state: InternalControllerState<T, TState>) {
  return { ...state, value: state.value?.[0] } as TState;
}
